/**
 * bridge/local_notification_controller.js — "local-notification" bridge component.
 *
 * Custom-tier bridge component: schedule a LOCAL (offline, no server) reminder —
 * e.g. "follow up on overdue invoice INV-42" at a chosen time. Distinct from the
 * push notification-token component: nothing leaves the device.
 *
 * Identifier "bridge--local-notification" (glob-derived from this subdir; wire with
 * data-controller="bridge--local-notification"). static component "local-notification"
 * MUST equal the iOS class var name
 * (native/ios/Chippie/Bridge/LocalNotificationComponent.swift) and the Android-reached
 * method names on window.ChippieNative.
 *
 * Why it extends Strada's BridgeComponent: the iOS scheduler is reached via
 * this.send("schedule"/"cancel", ...), which only works on a connected bridge
 * component inside the iOS Hotwire Native shell. Extending BridgeComponent gives us
 * that send() transport AND the UA-gated connect() (it only connects in a native shell).
 *
 * Three-way transport via the window.ChippieBridge facade. This controller registers
 * a `localNotification` impl set ({ ios, android, pwa }) onto the facade; the facade
 * (chippie-bridge.js) owns platform detection and picks the right impl per call.
 *
 * Facade API (both methods return a Promise):
 *
 *   await window.ChippieBridge.localNotification.schedule({
 *     id: "invoice-42-followup",   // stable id; re-scheduling the same id replaces it
 *     title: "Follow up on INV-42",
 *     body:  "This invoice is 7 days overdue.",
 *     timestamp: Date.now() + 3 * 86400 * 1000,  // epoch ms — when to fire
 *   });   // resolves true if scheduled, false if permission denied / past time
 *
 *   await window.ChippieBridge.localNotification.cancel("invoice-42-followup");
 *
 *   - ios:     this.send("schedule"/"cancel", payload, reply) over Strada to the
 *              Swift component; reply.data carries { scheduled } / { cancelled }.
 *   - android: window.ChippieNative.scheduleLocalNotification(id, title, body,
 *              timestamp) / cancelLocalNotification(id) — fire-and-forget
 *              @JavascriptInterface (it cannot return async), so schedule resolves
 *              true optimistically (the native side handles the runtime
 *              POST_NOTIFICATIONS grant and AlarmManager itself).
 *   - pwa:     no reliable background scheduler without a service-worker + push
 *              backend; resolves false so callers can fall back to a server-side
 *              reminder. (We deliberately do NOT setTimeout — it dies when the tab
 *              closes and would be a silent no-op reminder.)
 *
 * Template wiring:
 *   <div data-controller="bridge--local-notification"
 *        data-bridge--local-notification-id-value="invoice-42-followup"
 *        data-bridge--local-notification-title-value="Follow up on INV-42"
 *        data-bridge--local-notification-body-value="This invoice is 7 days overdue."
 *        data-bridge--local-notification-timestamp-value="1750000000000">
 *     <button data-action="bridge--local-notification#schedule">Remind me</button>
 *     <button data-action="bridge--local-notification#cancel">Cancel reminder</button>
 *   </div>
 * Or call window.ChippieBridge.localNotification.schedule({...}) directly.
 *
 * On success the controller dispatches "bridge--local-notification:scheduled"
 * (detail { id }) so a sibling controller can react without coupling to this one.
 */

import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Android: fire-and-forget @JavascriptInterface, wrapped in a resolved Promise.
function androidSchedule({ id, title, body, timestamp }) {
  if (!window.ChippieNative || !window.ChippieNative.scheduleLocalNotification) {
    return Promise.resolve(false);
  }
  try {
    window.ChippieNative.scheduleLocalNotification(id, title, body, timestamp);
    return Promise.resolve(true);
  } catch (_e) {
    return Promise.resolve(false);
  }
}

function androidCancel(id) {
  if (!window.ChippieNative || !window.ChippieNative.cancelLocalNotification) {
    return Promise.resolve(false);
  }
  try {
    window.ChippieNative.cancelLocalNotification(id);
    return Promise.resolve(true);
  } catch (_e) {
    return Promise.resolve(false);
  }
}

// Register the Android + PWA impls (and safe iOS placeholders). connect() swaps in
// the live Strada send inside the iOS shell.
function registerStandalone() {
  if (!window.ChippieBridge || !window.ChippieBridge.register) return;
  window.ChippieBridge.register("localNotification", {
    ios: {
      // Overridden by connect() with a real this.send inside the iOS shell. If
      // selected before connect (shouldn't happen — facade picks ios only inside
      // the shell), resolve falsey so callers don't hang.
      schedule: () => Promise.resolve(false),
      cancel: () => Promise.resolve(false),
    },
    android: { schedule: androidSchedule, cancel: androidCancel },
    pwa: {
      schedule: () => Promise.resolve(false),
      cancel: () => Promise.resolve(false),
    },
  });
}

if (window.ChippieBridge) {
  registerStandalone();
} else {
  document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
  static component = "local-notification";

  static values = {
    id: String,
    title: String,
    body: String,
    timestamp: Number,
  };

  connect() {
    super.connect();
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    // Inside the iOS shell: wire the real Strada sends into the facade, keeping the
    // Android/PWA fallbacks intact.
    window.ChippieBridge.register("localNotification", {
      ios: {
        schedule: (payload) => this.#iosSchedule(payload),
        cancel: (id) => this.#iosCancel(id),
      },
      android: { schedule: androidSchedule, cancel: androidCancel },
      pwa: {
        schedule: () => Promise.resolve(false),
        cancel: () => Promise.resolve(false),
      },
    });
  }

  disconnect() {
    // Leaving the iOS shell: drop the live-send impls back to the standalone
    // placeholders so a stale this.send is never invoked.
    registerStandalone();
    super.disconnect();
  }

  // data-action="bridge--local-notification#schedule" trigger — schedule from values.
  schedule(event) {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    const payload = {
      id: this.idValue,
      title: this.titleValue,
      body: this.bodyValue,
      timestamp: this.timestampValue,
    };
    window.ChippieBridge.localNotification
      .schedule(payload)
      .then((ok) => {
        if (ok) this.dispatch("scheduled", { detail: { id: payload.id } });
        return ok;
      })
      .catch(() => {
        /* swallow — callers act on the absent dispatch */
      });
  }

  // data-action="bridge--local-notification#cancel" trigger.
  cancel(event) {
    if (event) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
    window.ChippieBridge.localNotification.cancel(this.idValue).catch(() => {});
  }

  // iOS: send "schedule" over Strada; resolve when the Swift component replies.
  #iosSchedule(payload) {
    return new Promise((resolve) => {
      this.send("schedule", payload, (message) => {
        resolve(Boolean(message.data && message.data.scheduled));
      });
    });
  }

  #iosCancel(id) {
    return new Promise((resolve) => {
      this.send("cancel", { id }, (message) => {
        resolve(Boolean(message.data && message.data.cancelled));
      });
    });
  }
}
